import { Code, ConnectError } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { type ChangeEventHandler, useRef, useState } from "react";
import toast from "react-hot-toast";
import Upload from "~icons/material-symbols/upload";
import {
  CreateHokkaidoR8HouseholderCodeUsesAsStaffMatchRowError,
  CreateHokkaidoR8HouseholderCodeUsesAsStaffMatchRowError_Reason as Reason,
} from "schema/gen/es/chiikipoint/adminconsole/v2/service_pb";
import type { HokkaidoR8HouseholderMynaApplicationResult } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import { css } from "../../../../../styled-system/css";
import { LoadingOverlay } from "../../../../components/loading-overlay";
import {
  useCreateHokkaidoR8HouseholderCodeUsesAsStaffMatch,
  useExportHokkaidoR8HouseholderMynaApplications,
} from "../../../../libs/admin/hooks";
import {
  buildPeriodFilename,
  downloadCsvFile,
} from "../../../../libs/utils/csv";
import { button } from "../../../../styles/buttonStyles";
import { contentWrapper } from "../../../../styles/contentWrapper";
import {
  pageTitle,
  sectionTitle,
  sectionWrapper,
  titleWrapper,
} from "../../../../styles/sectionStyles";
import {
  table,
  tableCell,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from "../../../../styles/tableStyles";
import { ExportSection } from "../-components/export-section";
import {
  APPLICATION_ID_COLUMN,
  buildMynaApplicationCsv,
  decodeCsvBytes,
  formatResult,
  PENDING_RESULTS,
  type ParseResult,
  parseStaffMatchCsv,
  SERIAL_INPUT_COLUMN,
} from "./-csv/csv";

export const Route = createFileRoute("/admin/hokkaido/myna-applications/")({
  component: RouteComponent,
});

type LineError = {
  line: number;
  message: string;
};

type SubmitResult =
  | { kind: "idle" }
  | { kind: "success"; count: number }
  | { kind: "error"; errors: LineError[] }
  | { kind: "fatal"; message: string };

const formatReason = (reason: Reason): string => {
  switch (reason) {
    case Reason.MALFORMED_APPLICATION_ID:
      return "申請IDの形式が正しくありません";
    case Reason.MALFORMED_CODE:
      return "シリアルナンバーの形式が正しくありません";
    case Reason.DUPLICATE_APPLICATION_IN_REQUEST:
      return "CSV 内で重複している申請IDです";
    case Reason.DUPLICATE_CODE_IN_REQUEST:
      return "CSV 内で重複しているシリアルナンバーです";
    case Reason.APPLICATION_NOT_FOUND:
      return "存在しない申請IDです";
    case Reason.APPLICATION_NOT_PENDING:
      return "道庁確認待ちではない申請です（付与済みなど）";
    case Reason.CODE_NOT_FOUND:
      return "登録されていないシリアルナンバーです";
    case Reason.CODE_ALREADY_USED:
      return "既に使用済みのシリアルナンバーです";
    case Reason.USER_ALREADY_RECEIVED:
      return "申請者は既にポイントを受け取り済みです";
    default:
      return "不明なエラー";
  }
};

const fatalMessage = (error: unknown): string => {
  if (error instanceof ConnectError) {
    if (error.code === Code.PermissionDenied) {
      return "権限がありません。組織を確認してください。";
    }
    return error.message;
  }
  return error instanceof Error
    ? error.message
    : "通信に失敗しました。時間を置いて再度お試しください。";
};

function RouteComponent() {
  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>マイナポータル申請（道庁確認）</h1>
      </div>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        })}
      >
        <ExportApplications />
        <UploadStaffMatches />
      </div>
    </div>
  );
}

function ExportApplications() {
  const mutation = useExportHokkaidoR8HouseholderMynaApplications();
  const [results, setResults] = useState<
    HokkaidoR8HouseholderMynaApplicationResult[]
  >([]);

  const toggle = (
    result: HokkaidoR8HouseholderMynaApplicationResult,
    checked: boolean,
  ) =>
    setResults((prev) =>
      checked ? [...prev, result] : prev.filter((r) => r !== result),
    );

  return (
    <ExportSection
      title="道庁確認待ち申請 CSV出力"
      description={`指定期間内に申請（再申請を含む）された道庁確認待ちの申請を出力します。住民基本台帳で世帯主を特定できたら「${SERIAL_INPUT_COLUMN}」にシリアルナンバーを記入し、下の「照合結果の登録」で同じファイルを読み込んでください。`}
      isPending={mutation.isPending}
      run={async (start, end) => {
        const rows = await mutation.mutateAsync({ start, end, results });
        const csv = buildMynaApplicationCsv(rows);
        return {
          count: rows.length,
          download: () =>
            downloadCsvFile(
              buildPeriodFilename("myna_applications", start, end, "csv"),
              csv,
            ),
        };
      }}
    >
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        })}
      >
        <div className={css({ fontSize: "12px" })}>
          結果（未選択の場合はすべて）
        </div>
        <div
          className={css({ display: "flex", gap: "16px", flexWrap: "wrap" })}
        >
          {PENDING_RESULTS.map((result) => (
            <label
              key={result}
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "14px",
              })}
            >
              <input
                type="checkbox"
                checked={results.includes(result)}
                onChange={(e) => toggle(result, e.target.checked)}
                disabled={mutation.isPending}
                className={css({
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                })}
              />
              {formatResult(result)}
            </label>
          ))}
        </div>
      </div>
    </ExportSection>
  );
}

function UploadStaffMatches() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState("");
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitResult>({
    kind: "idle",
  });

  const mutation = useCreateHokkaidoR8HouseholderCodeUsesAsStaffMatch();

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = decodeCsvBytes(new Uint8Array(await file.arrayBuffer()));
      const result = await parseStaffMatchCsv(text);
      setParseResult(result);
      setFileName(file.name);
      setSubmitResult({ kind: "idle" });
      if (result.errors.length === 0 && result.items.length === 0) {
        toast(`${SERIAL_INPUT_COLUMN}が記入された行がありません`, {
          icon: "ℹ️",
        });
      }
    } catch (err) {
      console.error("CSV parsing failed", err);
      setParseResult(null);
      setFileName("");
      setSubmitResult({ kind: "idle" });
      toast.error(
        err instanceof Error ? err.message : "CSVの読み込みに失敗しました",
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRegister = async () => {
    if (
      !parseResult ||
      parseResult.errors.length > 0 ||
      parseResult.items.length === 0
    ) {
      return;
    }
    const { items } = parseResult;
    if (
      !window.confirm(
        `${items.length} 件の申請にポイントを付与します。付与後は取り消せません。よろしいですか？`,
      )
    ) {
      return;
    }
    setSubmitResult({ kind: "idle" });
    try {
      await mutation.mutateAsync({
        items: items.map(({ applicationId, code }) => ({
          applicationId,
          code,
        })),
      });
      setSubmitResult({ kind: "success", count: items.length });
    } catch (error) {
      console.error("Failed to register staff matches:", error);
      if (
        error instanceof ConnectError &&
        error.code === Code.FailedPrecondition
      ) {
        const details = error.findDetails(
          CreateHokkaidoR8HouseholderCodeUsesAsStaffMatchRowError,
        );
        if (details.length > 0) {
          const errors: LineError[] = details.map((d) => ({
            line: items[d.index]?.line ?? d.index + 2,
            message: formatReason(d.reason),
          }));
          errors.sort((a, b) => a.line - b.line);
          setSubmitResult({ kind: "error", errors });
          return;
        }
      }
      setSubmitResult({ kind: "fatal", message: fatalMessage(error) });
    }
  };

  const itemCount = parseResult?.items.length ?? 0;
  const canRegister =
    !!parseResult &&
    parseResult.errors.length === 0 &&
    itemCount > 0 &&
    !mutation.isPending &&
    submitResult.kind !== "success";

  return (
    <div className={sectionWrapper()}>
      <div className={sectionTitle()}>照合結果の登録</div>
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        })}
      >
        <p className={css({ fontSize: "14px", color: "text.secondary" })}>
          上で出力した CSV に記入したものを読み込みます。
          <code>{APPLICATION_ID_COLUMN}</code> と{" "}
          <code>{SERIAL_INPUT_COLUMN}</code>{" "}
          カラムだけを使い、その他のカラムは無視されます。
          {SERIAL_INPUT_COLUMN}が空欄の行は登録対象外です。1 行でも不備があると
          CSV 全体が登録対象外となり、どの申請にもポイントは付与されません。
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
          disabled={mutation.isPending}
        />
        <div>
          <button
            type="button"
            className={button({ color: "secondary" })}
            onClick={() => fileInputRef.current?.click()}
            disabled={mutation.isPending}
          >
            <Upload width="20" height="20" />
            CSVファイルを読み込む
          </button>
        </div>
        {fileName && (
          <p className={css({ fontSize: "14px", color: "text.secondary" })}>
            読み込み済み: {fileName}（{SERIAL_INPUT_COLUMN}記入済み {itemCount}{" "}
            件）
          </p>
        )}
        {parseResult && parseResult.errors.length > 0 && (
          <ErrorList title="CSV 解析時のエラー" errors={parseResult.errors} />
        )}
        {parseResult && parseResult.errors.length === 0 && itemCount > 0 && (
          <div className={css({ maxHeight: "320px", overflowY: "auto" })}>
            <div className={tableWrapper()}>
              <table className={table()}>
                <thead className={tableHead()}>
                  <tr>
                    <th className={tableHeadCell()}>行</th>
                    <th className={tableHeadCell()}>{APPLICATION_ID_COLUMN}</th>
                    <th className={tableHeadCell()}>シリアルナンバー</th>
                  </tr>
                </thead>
                <tbody>
                  {parseResult.items.map((item) => (
                    <tr key={item.line} className={tableRow()}>
                      <td className={tableCell()}>{item.line}</td>
                      <td className={tableCell()}>{item.applicationId}</td>
                      <td className={tableCell()}>{item.code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {submitResult.kind === "error" && (
          <ErrorList title="登録時のエラー" errors={submitResult.errors} />
        )}
        {submitResult.kind === "fatal" && (
          <div className={css({ color: "text.accentError", fontSize: "14px" })}>
            {submitResult.message}
          </div>
        )}
        {submitResult.kind === "success" && (
          <div className={css({ fontSize: "14px", color: "text.secondary" })}>
            {submitResult.count} 件の申請にポイントを付与しました。
          </div>
        )}
        <div>
          <button
            type="button"
            className={button({ color: "primary" })}
            onClick={handleRegister}
            disabled={!canRegister}
          >
            ポイント付与 {itemCount > 0 ? `(${itemCount}件)` : ""}
            {mutation.isPending && <LoadingOverlay size="sm" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorList({ title, errors }: { title: string; errors: LineError[] }) {
  return (
    <div
      className={css({
        color: "text.accentError",
        fontSize: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      })}
    >
      <span>
        {errors.length}件のエラーがあります（{title}）
      </span>
      <ul className={css({ listStyle: "disc", pl: "2em" })}>
        {errors.map((e, i) => (
          <li key={`${e.line}-${i}`}>
            {e.line}行目: {e.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
